// components/ProtectedRoute.js
import React from 'react';
import { useAuth } from '../App';

const AccessDenied = ({ userRole, requiredPage, requiredPermission }) => {
  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'manager':
      case 'organization_manager': return 'Organization Manager';
      case 'trainer': return 'Trainer';
      case 'student': return 'Student';
      default: return role ? role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ') : 'User';
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="bg-[#18181b] rounded-3xl shadow-2xl border border-zinc-800 p-8">
          <div className="w-20 h-20 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-red-500/20">
            <span className="text-red-500 text-3xl">🚫</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3 tracking-tight">Access Denied</h2>
          <p className="text-zinc-400 mb-8">You don't have permission to access this page.</p>
          <div className="bg-zinc-900/50 rounded-2xl p-6 mb-8 text-sm border border-zinc-800/50 text-left space-y-3">
            <p className="text-zinc-400"><span className="font-semibold text-zinc-300">Your role:</span> {getRoleDisplayName(userRole)}</p>
            {requiredPage && (
              <p className="text-zinc-400"><span className="font-semibold text-zinc-300">Required Page:</span> <code className="bg-zinc-800 px-2 py-1 rounded text-green-400">{requiredPage}</code></p>
            )}
            {requiredPermission && (
              <p className="text-zinc-400"><span className="font-semibold text-zinc-300">Required Permission:</span> <code className="bg-zinc-800 px-2 py-1 rounded text-blue-400">{requiredPermission}</code></p>
            )}
          </div>
          <button onClick={() => window.history.back()} className="w-full px-6 py-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all font-bold shadow-lg shadow-blue-600/20 active:scale-95">Go Back</button>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, requiredPage, requiredPermission }) => {
  const { user } = useAuth();
  if (!user) return null;

  const userRole = user.role;
  const userPages = user.pages || [];
  const userPermissions = user.permissions || [];

  // Super Admin bypass
  if (userRole === 'super_admin' || userPages.includes('*') || userPermissions.includes('*')) {
    return children;
  }

  // 1. Check Page Access
  if (requiredPage) {
    const hasPageAccess = userPages.includes(requiredPage);
    if (!hasPageAccess) {
      return <AccessDenied userRole={userRole} requiredPage={requiredPage} />;
    }
  }

  // 2. Check Permission Access (if applicable)
  if (requiredPermission) {
    const hasPermission = userPermissions.includes(requiredPermission);
    if (!hasPermission) {
      return <AccessDenied userRole={userRole} requiredPermission={requiredPermission} />;
    }
  }

  return children;
};

export default ProtectedRoute;